import { Flex, Box, VStack, Image, useColorModeValue, Link } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React from 'react';

import config from 'configs/app';
import useNavItems, { isGroupItem } from 'lib/hooks/useNavItems';
import getDefaultTransitionProps from 'theme/utils/getDefaultTransitionProps';
import IconSvg from 'ui/shared/IconSvg';
import useIsAuth from 'ui/snippets/auth/useIsAuth';
import NetworkMenu from 'ui/snippets/networkMenu/NetworkMenu';

import NavLink from './NavLink';
import NavLinkGroup from './NavLinkGroup';
import NavLinkRewards from './NavLinkRewards';

const NavigationDesktop = () => {
  const router = useRouter();

  const { mainNavItems, accountNavItems } = useNavItems();

  const isAuth = useIsAuth();
  const sidebarBg = useColorModeValue('#FFFFFF', '#111111');
  const sidebarBorder = useColorModeValue('#E9ECEF', '#222222');
  const logoSrc = useColorModeValue('/static/homepage/sidebar-logo-light.svg', '/static/homepage/sidebar-logo.svg');
  const madeWithColor = useColorModeValue('#666', '#999');
  const madeWithHoverColor = useColorModeValue('#000', '#fff');
  const blockscoutIconName = useColorModeValue('blockscout_black', 'blockscout');

  const isCollapsed = false;

  const goto = React.useCallback(() => {
    router.push({ pathname: '/' });
  }, [ router ]);

  return (
    <Flex
      display={{ base: 'none', lg: 'flex' }}
      role="group"
      position="sticky"
      top={ 0 }
      zIndex="sticky"
      flexDirection="column"
      alignItems="stretch"
      bg={ sidebarBg }
      borderRight="1px solid"
      borderColor={ sidebarBorder }
      pl="24px"
      pr="24px"
      pt="8px"
      pb="0"
      width="240px"
      minW="240px"
      h="100vh"
      { ...getDefaultTransitionProps({ transitionProperty: 'width, padding' }) }
    >
      <Box
        as="header"
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        flexDirection="row"
        w="100%"
        h="32px"
        transitionProperty="padding"
        transitionDuration="normal"
        transitionTimingFunction="ease"
      >
        <Box as="div" sx={{
          color: '#27D1A0',
          display: 'Flex',
          cursor: 'pointer',
        }}
        onClick={ goto }
        >
          <Box
            display="block"
            style={{ width: '146px', height: '32px', overflow: 'hidden' }}>
            <Image src={ logoSrc } alt="BOT Chain Explorer" w="146px" h="32px"/>
          </Box>
        </Box>
        { Boolean(config.UI.navigation.featuredNetworks) && <NetworkMenu isCollapsed={ isCollapsed }/> }
      </Box>

      <Box as="nav" mt="40px" w="100%">
        <VStack as="ul" spacing="12px" alignItems="flex-start">
          { mainNavItems.map((item) => {
            if (isGroupItem(item)) {
              return <NavLinkGroup key={ item.text } item={ item } isCollapsed={ isCollapsed }/>;
            } else {
              return <NavLink key={ item.text } item={ item } isCollapsed={ isCollapsed }/>;
            }
          }) }
        </VStack>
      </Box>
      { isAuth && (
        <Box as="nav" borderTopWidth="1px" borderColor="divider" w="100%" mt={ 3 } pt={ 3 }>
          <VStack as="ul" spacing="1" alignItems="flex-start">
            <NavLinkRewards isCollapsed={ isCollapsed }/>
            { accountNavItems.map((item) => <NavLink key={ item.text } item={ item } isCollapsed={ isCollapsed }/>) }
          </VStack>
        </Box>
      ) }

      <Link
        href="https://www.blockscout.com"
        target="_blank"
        rel="noopener noreferrer"
        display="flex"
        alignItems="center"
        columnGap={ 2 }
        color={ madeWithColor }
        fontSize="14px"
        marginTop="auto"
        marginBottom="40px"
        _hover={{ color: madeWithHoverColor, textDecoration: 'none' }}
      >
        <span>Made with</span>
        <IconSvg
          name={ blockscoutIconName }
          height="14px"
          width="100px"
        />
      </Link>
    </Flex>
  );
};

export default NavigationDesktop;
